/**
 * Lazyloading module class.
 * 
 * @author Lennart Bergmann <lb@sopg.de>
 * @author sh@sopg.de
 * 
 * @jira ZFDS-59
 * @jira ZFDS-60
 * @jira FDZ-620
 * 
 * @created 2021-06-10
 * @updated 2021-07-01
 * @updated 2023-01-19
 * 
 * @returns LazyLoading
 */

define('module/lazy-loading',[
    'jquery',
    'underscore',
    'cnf',
    'IntersectionObserverPolyfill',
    '_pubsub'
], function($,_,Cnf, Flickity, Pubsub) {
    'use strict';
    
    return function()
    {
        /**
         * Constructor
         * 
         * @author Lennart Bergmann <lb@sopg.de>
         * 
         * @created 2021-06-10
         * @updated 2021-06-11
         * 
         * @returns {LazyLoading}
         */
        this.o = {
            sel : {
                lazyImg     : '.js-lazy-img',
                lazyBgImg   : '.js-lazy-bg-img',
                lazyPosterImg : '.js-lazy-poster-img',
                lazyVideo : '.js-lazy-video',
                deferredImg : '.js-deferred-img',
                readyImg : '.js-ready-img'
            },
            opt : {
                rootMargin     : '500px',
                thresholdSteps : 10
            }
        };

        /**
         * Initializes lazyloading.
         * 
         * @author Lennart Bergmann <lb@sopg.de>
         * @author sh@sopg.de
         * 
         * @jira ZFDS-59
         * @jira ZFDS-60
         * @jira FDZ-655
         * 
         * @created 2021-06-10
         * @updated 2021-07-01
         * @updated 2023-05-12
         * 
         * @returns {LazyLoading}
         */
        this.initialize = function()
        {
            /* Init observers. */
            this.initImageObserver();
            
            /* Added 2021-07-01, ZFDS-60:
            Re-initialize the image observer when 
            "lazyload" event is fired on window. */
            $(window).on('lazyload', _.bind(this.initImageObserver, this));

            /**
             * FDZ-655
             * @added 2023-05-12
             * @see event listener "lazyload"
             * since dispatch event passes Event Object itself as first parameter, we neeed an intermediate step
             */
            $(window).on('dispatchLazyload', _.bind(function(ev, data) {
                _.bind(this.initImageObserver, this)(data.el);
            }, this));

            /* Attach load event listener. */
            window.addEventListener('load', _.bind(this.loadDeferredImages, this));

            /* In case the 'load' event fired 
            before the listener was attached. */
            if (document.readyState === 'complete') {
                
                this.loadDeferredImages();
            }

            return this;
        };


        /**
         * Load deferred images.
         * Deferred images are loaded instantly when window.onload fires.
         * 
         * @author Lennart Bergmann <lb@sopg.de>
         * 
         * @created 2021-06-10
         * 
         * @returns {LazyLoading}
         */
        this.loadDeferredImages = function()
        {
            const $deferredImages = $(this.o.sel.deferredImg);

            _.each($deferredImages, function(oContainer) {
                /* Load images. */
                this.loadImages(oContainer);
            });

            return this;
        };


        /**
         * Observes and loads all lazy images.
         * 
         * @author Lennart Bergmann <lb@sopg.de>
         * @jira ZFDS-59
         * 
         * @created 2021-06-10
         * @updated 2021-07-01
         * 
         * @param {DOMNode|String} mContainer 
         * @param {String} sClass
         * @returns {LazyLoading}
         */
        this.initImageObserver = function(mContainer, sClass)
        {
            if ("IntersectionObserver" in window) 
            {
                if(typeof mContainer === 'undefined') {
                    mContainer = 'body';
                }

                if(typeof sClass === 'undefined') {
                    sClass = this.o.sel.lazyImg + ',' + this.o.sel.lazyBgImg + ',' + this.o.sel.lazyVideo + ',' + this.o.sel.lazyPosterImg
                }
                
                var $lazyImages;
                const $container = $(mContainer);

                if ($container.hasClass(sClass.replace('.', ''))) {
                    
                    $lazyImages = $container;
                    
                } else {
                    
                    $lazyImages = $container.find(sClass);
                }

                const oLazyImageObserver = new IntersectionObserver(_.bind(function (aEntries, oObserver) {
                    aEntries.forEach(_.bind(function (oEntry) {
                        if (oEntry.intersectionRatio > 0) {

                            /* Load images. */
                            this.loadImages(oEntry.target);
            
                            /* Unobserve. */
                            oLazyImageObserver.unobserve(oEntry.target);
                        }
                    }, this));
                }, this),
                /**
                 * Options
                 * rootMargin: Grow or shrink the intersection area
                 * threshold : Visible area ratios of element at which to trigger
                 */
                { 
                    rootMargin : this.o.opt.rootMargin, 
                    threshold  : this.buildThresholdArray(this.o.opt.thresholdSteps)
                });

                /**
                 * Observe all lazy images
                 */
                _.each($lazyImages, function(oLazyImage) {
                    oLazyImageObserver.observe(oLazyImage);
                });
            }

            return this;
        };
        

        /**
         * Loads all images in given container.
         * 
         * @author Lennart Bergmann <lb@sopg.de>
         * 
         * @created 2021-06-10
         * @updated 2021-06-11
         * @jira ZFDS-59
         * 
         * @param {object} oContainer
         * 
         * @returns {LazyLoading}
         */
        this.loadImages = function(oContainer) 
        {
            const $container = $(oContainer);

            if ($container.hasClass(this.o.sel.lazyImg.replace('.', '')))
            {
                _.each($container.find('img, source'), function(oImg) {
                    if (oImg.dataset.src) {
                        
                        oImg.src = oImg.dataset.src;
                        oImg.removeAttribute('data-src');
                        
                    } else
                    if (oImg.dataset.srcset) {
                        
                        oImg.srcset = oImg.dataset.srcset;
                        oImg.removeAttribute('data-srcset');
                    }
                });
            } else if ($container.hasClass(this.o.sel.lazyBgImg.replace('.', '')))
            {
                
                if (oContainer.dataset.src) {
                    
                    $container.css('background-image', 'url(' + oContainer.dataset.src + ')');
                    oContainer.removeAttribute('data-src');
                    $container.removeClass(this.o.sel.lazyBgImg.replace('.', ''));
                }
                
            } else if ($container.hasClass(this.o.sel.lazyVideo.replace('.', ''))) 
            {

                /* set new src and srcset from its data attribute */
                _.each($container.find('source'), function(oVideo) {
                    if (oVideo.dataset.src) {
                        oVideo.src = oVideo.dataset.src;
                        oVideo.removeAttribute('data-src');
                    }
                    if (oVideo.dataset.srcset) {
                        oVideo.srcset = oVideo.dataset.srcset;
                        oVideo.removeAttribute('data-srcset');
                    }
                });

                /* load and play video after set src and srcset */
                var video = $container.get(0);
                video.load();
                video.play();

                /* set preview/fallback image*/
                if ($container.attr('data-poster')) {
                    $container.attr('poster',$container.attr('data-poster'));
                    $container.removeAttr('data-poster');
                }
            }

            /* Remove classes. */
            $container.removeClass(this.o.sel.lazyImg.replace('.', ''));
            $container.removeClass(this.o.sel.lazyVideo.replace('.', ''));
            $container.removeClass(this.o.sel.lazyPosterImg.replace('.', ''));
            $container.removeClass(this.o.sel.deferredImg.replace('.', ''));
            $container.addClass(this.o.sel.readyImg.replace('.', ''));

            return this;
        };


        /**
         * Builds an array of Thresholds.
         * 
         * @author Lennart Bergmann <lb@sopg.de>
         * 
         * @created 2019-07-15
         * @jira DDSP-443
         * 
         * @param {int} iNumSteps
         * @returns {array}
         */
        this.buildThresholdArray = function(iNumSteps) 
        {
            const aThresholds = [];
            var fRatio = 0.0;

            for (var i = 1.0; i <= iNumSteps; i++) {
                fRatio = i/iNumSteps;
                aThresholds.push(fRatio);
            }

            return aThresholds;
        };

        this.dispatchLazyload = function(ev)
        {

        }

        this.initialize();
    };
});
